/**
 * Google Sign-In button + first-run "Complete your profile" modal.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
import React, { useEffect, useState } from "react";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { parsePhoneNumberFromString, AsYouType, getCountryCallingCode } from "libphonenumber-js";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, CheckCircle2 } from "lucide-react";

type CompletionPayload = {
  token: string;
  user: any;
  needs_completion: boolean;
  missing_fields: string[];
};

const POPULAR_COUNTRIES = [
  { code: "IN", label: "🇮🇳 India (+91)" },
  { code: "US", label: "🇺🇸 United States (+1)" },
  { code: "GB", label: "🇬🇧 United Kingdom (+44)" },
  { code: "AE", label: "🇦🇪 UAE (+971)" },
  { code: "SG", label: "🇸🇬 Singapore (+65)" },
  { code: "AU", label: "🇦🇺 Australia (+61)" },
  { code: "CA", label: "🇨🇦 Canada (+1)" },
];

const ProfileCompletionModal: React.FC<{
  open: boolean;
  onDone: () => void;
  suggestedName?: string;
}> = ({ open, onDone, suggestedName }) => {
  const [country, setCountry] = useState<string>("IN");
  const [phoneLocal, setPhoneLocal] = useState("");
  const [name, setName] = useState(suggestedName || "");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { setName(suggestedName || ""); }, [suggestedName]);

  const dial = getCountryCallingCode(country as any);
  const formatted = new AsYouType(country as any).input(phoneLocal);
  const e164 = "+" + dial + phoneLocal.replace(/\D/g, "");
  const parsed = parsePhoneNumberFromString(e164);
  const valid = !!parsed?.isValid();

  const submit = async () => {
    if (!valid) {
      toast.error("Please enter a valid mobile number");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/profile/complete", {
        phone_e164: e164,
        country,
        name: name.trim() || undefined,
      });
      toast.success("Welcome to SociBaba!");
      onDone();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* modal is mandatory */ }}>
      <DialogContent className="max-w-md" data-testid="profile-completion-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">One quick thing 👋</DialogTitle>
          <DialogDescription>
            Your account is signed in — we just need a mobile number to alert you when SIBA scores hit your threshold and to protect your workspace with 2FA later. Takes 10 seconds.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--sg-muted)] mb-1">Full name</div>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="profile-name-input" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--sg-muted)] mb-1">Mobile</div>
            <div className="flex gap-2">
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="border border-[color:var(--sg-border)] rounded-md text-sm px-2 bg-white"
                data-testid="profile-country-select"
              >
                {POPULAR_COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
              <Input
                type="tel"
                value={formatted || phoneLocal}
                onChange={(e) => setPhoneLocal(e.target.value)}
                placeholder="98765 43210"
                className="flex-1"
                data-testid="profile-phone-input"
              />
            </div>
            {valid && <div className="text-xs text-emerald-600 mt-1 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> {e164}</div>}
          </div>
          <Button
            onClick={submit}
            disabled={!valid || submitting}
            className="w-full bg-[color:var(--sb-saffron)] hover:bg-[color:var(--sb-saffron-hover)] text-white"
            data-testid="profile-complete-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
            Finish setup
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const GoogleSignInButton: React.FC<{ mode?: "signin" | "signup"; onDone?: () => void }> = ({ mode = "signin", onDone }) => {
  const nav = useNavigate();
  const auth = useAuth();
  const { resolved: themeMode } = useTheme();
  const [clientId, setClientId] = useState<string>("");
  const [needCompletion, setNeedCompletion] = useState(false);
  const [suggestedName, setSuggestedName] = useState<string | undefined>();

  useEffect(() => {
    api.get("/auth/google/config").then((r) => setClientId(r.data.client_id)).catch(() => {});
  }, []);

  if (!clientId) return null;  // Google not configured on this deployment

  const handleCredential = async (credential: string) => {
    try {
      const { data } = await api.post<CompletionPayload>("/auth/google", { credential });
      localStorage.setItem("token", data.token);
      // Rehydrate the AuthContext with the new token
      if ((auth as any).loginWithToken) {
        await (auth as any).loginWithToken(data.token, data.user);
      } else {
        // Fallback — reload once so the AuthContext picks up localStorage
        setSuggestedName(data.user?.name);
      }
      if (data.needs_completion) {
        setSuggestedName(data.user?.name);
        setNeedCompletion(true);
      } else {
        toast.success(`Welcome back, ${data.user?.name || "friend"}!`);
        onDone?.();
        nav("/dashboard");
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Google sign-in failed");
    }
  };

  return (
    <div className="w-full">
      <GoogleOAuthProvider clientId={clientId}>
        <div data-testid={`google-signin-${mode}`}>
          <GoogleLogin
            onSuccess={(resp) => resp.credential && handleCredential(resp.credential)}
            onError={() => toast.error("Google sign-in was cancelled")}
            useOneTap={false}
            width="320"
            text={mode === "signup" ? "signup_with" : "signin_with"}
            shape="pill"
            theme={themeMode === "dark" ? "filled_black" : "outline"}
          />
        </div>
      </GoogleOAuthProvider>
      <ProfileCompletionModal
        open={needCompletion}
        suggestedName={suggestedName}
        onDone={() => {
          setNeedCompletion(false);
          onDone?.();
          nav("/dashboard");
        }}
      />
    </div>
  );
};

export default GoogleSignInButton;
